/**
 * One place that finds artwork for any track (Gordon 2026-10-31: "missing
 * thumbnails right thru the whole app … resolved once and for all").
 *
 * Many works have no cover of their own — it lives on their release, and a
 * page that forgot to look it up showed a letter tile. This loads every
 * release and artist ONCE per visit (both are small, public collections) and
 * resolves: own artwork → release cover (by work id) → release matched by
 * title + artist → artist photo. Writes on release save also copy the cover
 * onto the tracks (see propagateReleaseCover), so the data fills itself.
 */

import { db } from '@/lib/firebase';
import { collection, doc, getDocs, writeBatch, getDoc } from 'firebase/firestore';

const norm = (s: unknown) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/^\d+\s*[-.]?\s*/, '').replace(/[^a-z0-9]+/g, ' ').trim();

export const ownArtwork = (x: any): string =>
  x?.coverArtUrl || x?.imageUrl || (Array.isArray(x?.images) && x.images[0]) || x?.thumbnailUrl || x?.coverImageUrl || '';

export interface CoverIndex {
  /** Best artwork for a work/track-like object, '' when there is none anywhere. */
  forWork(w: any): string;
  /** The release a work sits on (for breadcrumbs), null when none. */
  releaseForWork(w: any): { id: string; title: string } | null;
}

let cached: Promise<CoverIndex> | null = null;

export function getCoverIndex(): Promise<CoverIndex> {
  if (cached) return cached;
  cached = (async () => {
    const [rels, arts] = await Promise.all([
      getDocs(collection(db, 'releases')).catch(() => null),
      getDocs(collection(db, 'artists')).catch(() => null),
    ]);
    const byWork = new Map<string, string>();
    const byTitle = new Map<string, string>();
    const relByWork = new Map<string, { id: string; title: string }>();
    const coverByRelease = new Map<string, string>(); // release id → its cover
    const relByTitle = new Map<string, { id: string; title: string }>();
    rels?.docs.forEach(d => {
      const r = d.data() as any;
      if (!r.adminHidden && r.hidden !== true) {
        const rel = { id: d.id, title: r.title || '' };
        const relArtist = norm(r.artist?.name || r.artistName || r.artist);
        (Array.isArray(r.tracks) ? r.tracks : []).forEach((t: any) => {
          const wid = t?.workId || t?.id;
          if (wid && !relByWork.has(wid)) relByWork.set(wid, rel);
          const k = `${norm(t?.title)}|${relArtist}`;
          if (!relByTitle.has(k)) relByTitle.set(k, rel);
        });
      }
      const c = ownArtwork(r);
      if (!c) return;
      coverByRelease.set(d.id, c);
      const artist = norm(r.artist?.name || r.artistName || r.artist);
      (Array.isArray(r.tracks) ? r.tracks : []).forEach((t: any) => {
        const wid = t?.workId || t?.id;
        if (wid && !byWork.has(wid)) byWork.set(wid, c);
        const k = `${norm(t?.title)}|${artist}`;
        if (!byTitle.has(k)) byTitle.set(k, c);
      });
    });
    const artistById = new Map<string, string>();
    const artistByName = new Map<string, string>();
    arts?.docs.forEach(d => {
      const a = d.data() as any;
      const img = a.imageUrl || (Array.isArray(a.images) && a.images[0]) || '';
      if (!img) return;
      artistById.set(d.id, img);
      artistByName.set(norm(a.name), img);
    });
    return {
      forWork(w: any) {
        const own = ownArtwork(w);
        if (own) return own;
        const id = w?.id || w?.workId;
        const artist = w?.artist?.name || w?.artistName || (typeof w?.artist === 'string' ? w.artist : '');
        return (id && byWork.get(id))
          || (id && coverByRelease.get(id))
          || (w?.workId && byWork.get(w.workId))
          || byTitle.get(`${norm(w?.title)}|${norm(artist)}`)
          || (w?.artistId && artistById.get(w.artistId))
          || artistByName.get(norm(artist))
          || '';
      },
      releaseForWork(w: any) {
        const id = w?.id || w?.workId;
        const artist = w?.artist?.name || w?.artistName || (typeof w?.artist === 'string' ? w.artist : '');
        return (w?.releaseId && rels?.docs.some(d => d.id === w.releaseId)
            ? { id: w.releaseId, title: (rels!.docs.find(d => d.id === w.releaseId)!.data() as any).title || '' }
            : null)
          || (id && relByWork.get(id))
          || (w?.workId && relByWork.get(w.workId))
          || relByTitle.get(`${norm(w?.title)}|${norm(artist)}`)
          || null;
      },
    };
  })();
  // A failed load must not stick for the whole visit.
  cached.catch(() => { cached = null; });
  return cached;
}

/** Forget the cached index (after a cover was changed in this tab). */
export function resetCoverIndex() { cached = null; }

/**
 * Copy a release's cover onto its tracks, so the cover shows on every page
 * without a lookup: tracks with no artwork, tracks showing an artist-photo
 * stand-in, and tracks that carry a copy of this release's earlier cover —
 * when the release cover changes, those follow it (Gordon 2026-11-03). A
 * track's own, different artwork is never touched. Best-effort — only the
 * track owner or an admin may write; anything else is skipped quietly.
 */
export async function propagateReleaseCover(
  release: { coverArtUrl?: string; images?: string[]; imageUrl?: string; tracks?: any[] },
  opts: { previousCovers?: string[] } = {},
) {
  const cover = ownArtwork(release);
  const ids = (Array.isArray(release.tracks) ? release.tracks : []).map(t => t?.workId || t?.id).filter(Boolean) as string[];
  if (!cover || ids.length === 0) return;
  // Pictures that belong to the release: a track showing one of them shows a copy.
  const releasePictures = new Set([...(opts.previousCovers || []), ...(Array.isArray(release.images) ? release.images : []), release.coverArtUrl, release.imageUrl].filter(Boolean) as string[]);
  try {
    const snaps = await Promise.all(ids.map(id => getDoc(doc(db, 'works', id)).catch(() => null)));
    const batch = writeBatch(db);
    let n = 0;
    snaps.forEach(s => {
      const w = s?.exists() ? (s.data() as any) : null;
      if (!s || !w) return;
      const mine = ownArtwork(w);
      const standIn = w.imageSource === 'artist' && !w.coverArtUrl;
      // Only a copy of THIS release's pictures follows — a track that is also on
      // a single keeps whatever cover the other release gave it.
      const copy = !!mine && mine !== cover && releasePictures.has(mine);
      if (!mine || standIn || copy) { batch.update(s.ref, { coverArtUrl: cover, imageSource: 'release' }); n++; }
    });
    if (n) await batch.commit();
    resetCoverIndex();
  } catch (e) {
    console.warn('[cover-index] could not copy the release cover onto its tracks', e);
  }
}

/** Same as propagateReleaseCover, reading the saved release by id first. */
export async function propagateReleaseCoverById(releaseId: string) {
  try {
    const snap = await getDoc(doc(db, 'releases', releaseId));
    if (snap.exists()) await propagateReleaseCover(snap.data() as any);
  } catch { /* best-effort */ }
}
